import { BOOLEAN_BYTES_PER_ELEMENT, BusMessage, MessageFactory, UID_BYTES_PER_ELEMENT, calcArrayByteLength } from "flavr-common";
/**
 * Message request to compute intersection between a ray and all collision shapes. Then returning the closest
 * intersection in a `ResponseRaycastClosestIntersectionMessage`
 */
export class RequestRaycastClosestIntersectionMessage extends BusMessage {
    get byteLength() {
        return RequestRaycastClosestIntersectionMessage.MAX_BYTE_LENGTH +
            calcArrayByteLength(this.params.targetModelUIDs); // targetModelUIDs
    }
    constructor(origin = [0, 0, 0], direction = [0, 0, 0], params = {}) {
        super();
        this.origin = origin;
        this.direction = direction;
        this.params = params;
    }
    serialization(stream) {
        this.origin = stream.number3(this.origin);
        this.direction = stream.number3(this.direction);
        if (stream.isReading) {
            this.params = {};
        }
        this.params.backfaceCulling = stream.booleanSafe(this.params.backfaceCulling);
        this.params.includeTriggers = stream.booleanSafe(this.params.includeTriggers);
        this.params.targetModelUIDs = stream.arraySafe(this.params.targetModelUIDs);
    }
    static is(msg) {
        return msg.type === RequestRaycastClosestIntersectionMessage.typestatic;
    }
}
RequestRaycastClosestIntersectionMessage.typestatic = "RaycastClosestMessage";
RequestRaycastClosestIntersectionMessage.subjectstatic = "selection";
RequestRaycastClosestIntersectionMessage.MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3 + // origin
    Float32Array.BYTES_PER_ELEMENT * 3 + // target
    BOOLEAN_BYTES_PER_ELEMENT + // backfaceCulling,
    BOOLEAN_BYTES_PER_ELEMENT; // includeTriggers
MessageFactory.register(RequestRaycastClosestIntersectionMessage);
/**
 * Message response that returns information on the intersection between a ray and all collision shapes
 */
export class ResponseRaycastClosestIntersectionMessage extends BusMessage {
    get byteLength() {
        let byteLength = BOOLEAN_BYTES_PER_ELEMENT; // isDefined
        if (this.response !== undefined) {
            byteLength += ResponseRaycastClosestIntersectionMessage.MAX_BYTE_LENGTH;
        }
        return byteLength;
    }
    constructor(response) {
        super();
        this.response = response;
    }
    serialization(stream) {
        const isDefined = stream.boolean(this.response !== undefined);
        if (stream.isReading && isDefined) {
            this.response = {
                uid: 0,
                distance: 0,
                worldPosition: [0, 0, 0],
                localPosition: [0, 0, 0],
                worldNormal: [0, 0, 0],
                localNormal: [0, 0, 0]
            };
        }
        if (this.response !== undefined) {
            this.response.uid = stream.uid(this.response.uid);
            this.response.distance = stream.float32(this.response.distance);
            this.response.worldPosition = stream.number3(this.response.worldPosition);
            this.response.localPosition = stream.number3(this.response.localPosition);
            this.response.worldNormal = stream.number3(this.response.worldNormal);
            this.response.localNormal = stream.number3(this.response.localNormal);
        }
    }
    static is(msg) {
        return msg.type === ResponseRaycastClosestIntersectionMessage.typestatic;
    }
}
ResponseRaycastClosestIntersectionMessage.typestatic = "RaycastResponseMessage";
ResponseRaycastClosestIntersectionMessage.subjectstatic = "selection";
ResponseRaycastClosestIntersectionMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Float32Array.BYTES_PER_ELEMENT * 3 + // distance
    Float32Array.BYTES_PER_ELEMENT * 3 + // worldPosition
    Float32Array.BYTES_PER_ELEMENT * 3 + // localPosition
    Float32Array.BYTES_PER_ELEMENT * 3 + // worldNormal
    Float32Array.BYTES_PER_ELEMENT * 3; // localNormal
MessageFactory.register(ResponseRaycastClosestIntersectionMessage);
/**
 * Message request to compute intersection between a primitive shape and all collision shapes. Then returning a list of
 * all collision shapes that intersect with that shape in a `RequestIntersectionsWithCollisionShapeMessage`
 */
export class RequestIntersectionsWithPrimitiveCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return (Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 1) + // position, rotation, scale
            Uint8Array.BYTES_PER_ELEMENT + // volume
            Float32Array.BYTES_PER_ELEMENT * 2 + // maxDistance, maxConeRadius
            BOOLEAN_BYTES_PER_ELEMENT // computeHitpoint
        );
    }
    constructor(position, rotation, scale, volume, maxDistance, maxConeRadius, computeHitpoint) {
        super();
        this.position = position;
        this.rotation = rotation;
        this.scale = scale;
        this.volume = volume;
        this.maxDistance = maxDistance;
        this.maxConeRadius = maxConeRadius;
        this.computeHitpoint = computeHitpoint;
    }
    serialization(stream) {
        var _a;
        this.position = stream.number3(this.position);
        this.rotation = stream.number4(this.rotation);
        this.scale = stream.float32(this.scale);
        this.volume = stream.uint8(this.volume);
        this.maxDistance = stream.float32(this.maxDistance);
        this.maxConeRadius = stream.float32Safe(this.maxConeRadius);
        this.computeHitpoint = stream.boolean((_a = this.computeHitpoint) !== null && _a !== void 0 ? _a : false);
    }
    static is(msg) {
        return msg.type === RequestIntersectionsWithPrimitiveCollisionShapeMessage.typestatic;
    }
}
RequestIntersectionsWithPrimitiveCollisionShapeMessage.typestatic = "RequestIntersectionsWithPrimitiveCollisionShapeMessage";
RequestIntersectionsWithPrimitiveCollisionShapeMessage.subjectstatic = "selection";
MessageFactory.register(RequestIntersectionsWithPrimitiveCollisionShapeMessage);
/**
 * Message response that returns all collision shapes that intersect with a primitive shape
 */
export class ResponseIntersectionsWithPrimitiveCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // length
            ResponseIntersectionsWithPrimitiveCollisionShapeMessage.DetectedSelectableByteLength * this.selectables.length);
    }
    constructor(selectables) {
        super();
        this.selectables = selectables;
    }
    serialization(stream) {
        const length = stream.uint32(this.selectables.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading) {
                this.selectables[i] = { uid: 0, distance: 0, relativePosition: [0, 0, 0], worldPosition: [0, 0, 0] };
            }
            const data = this.selectables[i];
            data.uid = stream.uid(data.uid);
            data.distance = stream.float32(data.distance);
            data.relativePosition = stream.number3(data.relativePosition);
            data.worldPosition = stream.number3(data.worldPosition);
        }
    }
    static is(msg) {
        return msg.type === ResponseIntersectionsWithPrimitiveCollisionShapeMessage.typestatic;
    }
}
ResponseIntersectionsWithPrimitiveCollisionShapeMessage.typestatic = "ResponseIntersectionsWithPrimitiveCollisionShapeMessage";
ResponseIntersectionsWithPrimitiveCollisionShapeMessage.subjectstatic = "selection";
ResponseIntersectionsWithPrimitiveCollisionShapeMessage.DetectedSelectableByteLength = UID_BYTES_PER_ELEMENT +
    Float32Array.BYTES_PER_ELEMENT + // distance
    Float32Array.BYTES_PER_ELEMENT * 3 + // relativePosition
    Float32Array.BYTES_PER_ELEMENT * 3; // worldPosition
MessageFactory.register(ResponseIntersectionsWithPrimitiveCollisionShapeMessage);
/**
 * Message request to test a test model against the provided list of target models, if no target models were provided
 * a test against all registered models with enabled collision shapes is performed instead. The test model as well as
 * all target models have to have collision shapes otherwise no intersection tests will be performed. If a list of
 * target models is provided, they are tested for intersection, regardless if they are enabled or disabled. The result
 * of the intersection test is returned in a `ResponseIntersectionsWithCollisionShapeMessage`
 */
export class RequestIntersectionsWithCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // testModelUID
            calcArrayByteLength(this.targetModelUIDs) // targetModelUIDs
        );
    }
    constructor(testModelUID, targetModelUIDs) {
        super();
        this.testModelUID = testModelUID;
        this.targetModelUIDs = targetModelUIDs;
    }
    serialization(stream) {
        this.testModelUID = stream.uid(this.testModelUID);
        this.targetModelUIDs = stream.arraySafe(this.targetModelUIDs);
    }
    static is(msg) {
        return msg.type === RequestIntersectionsWithCollisionShapeMessage.typestatic;
    }
}
RequestIntersectionsWithCollisionShapeMessage.typestatic = "RequestIntersectionsWithCollisionShapeMessage";
RequestIntersectionsWithCollisionShapeMessage.subjectstatic = "selection";
MessageFactory.register(RequestIntersectionsWithCollisionShapeMessage);
/**
 * Message response that returns all target models that intersect with the provided test model
 */
export class ResponseIntersectionsWithCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // length
            ResponseIntersectionsWithCollisionShapeMessage.byteLength * this.intersections.length);
    }
    constructor(intersections) {
        super();
        this.intersections = intersections;
    }
    serialization(stream) {
        const length = stream.uint32(this.intersections.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading) {
                this.intersections[i] = { uid: 0, distance: 0, relativePosition: [0, 0, 0], worldPosition: [0, 0, 0] };
            }
            const data = this.intersections[i];
            data.uid = stream.uid(data.uid);
            data.distance = stream.float32(data.distance);
            data.relativePosition = stream.number3(data.relativePosition);
            data.worldPosition = stream.number3(data.worldPosition);
        }
    }
    static is(msg) {
        return msg.type === ResponseIntersectionsWithCollisionShapeMessage.typestatic;
    }
}
ResponseIntersectionsWithCollisionShapeMessage.typestatic = "ResponseIntersectionsWithCollisionShapeMessage";
ResponseIntersectionsWithCollisionShapeMessage.subjectstatic = "selection";
ResponseIntersectionsWithCollisionShapeMessage.byteLength = UID_BYTES_PER_ELEMENT +
    Float32Array.BYTES_PER_ELEMENT + // distance
    Float32Array.BYTES_PER_ELEMENT * 3 + // relativePosition
    Float32Array.BYTES_PER_ELEMENT * 3; // worldPosition
MessageFactory.register(ResponseIntersectionsWithCollisionShapeMessage);
